// Need to use the React-specific entry point to import createApi
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  PaymentRequest,
  PaymentResponse,
  TransformedPaymentResponse,
} from '@shared/types';
import { transformResponse } from '@utilities/api';
import fetchBaseQuery from '@utilities/fetchBaseQuery';
import { formatStartDate, isValidDate } from '@utilities/helpers/common';

const transformRequestBody = ({
  primaryLocation,
  plan,
  licensePlate,
  atav,
  startDate,
  ...body
}) => ({
  location: primaryLocation?.uid || null,
  plan,
  license_plate: licensePlate,
  access_all: atav,
  start_date: startDate || null,
  ...body,
});

// Define a service using a base URL and expected endpoints
export const checkoutApi = createApi({
  reducerPath: 'checkoutApi',
  tagTypes: ['Membership', 'Invoice'],
  baseQuery: fetchBaseQuery,
  endpoints: (builder) => ({
    /**
     * Get summaries
     */
    getSummaries: builder.mutation({
      query: ({ ids = [], coupons = [], vat, terminalSession }) => ({
        url: `membership/summaries`,
        method: 'POST',
        body: {
          membershipIds: ids,
          coupons: coupons.join(','),
          vat: vat ? 0 : 1,
          terminalSession,
        },
      }),
      transformResponse,
    }),

    /**
     * Create membership
     */
    createMembership: builder.mutation({
      query: ({ loyalty, ...body }) => ({
        url: `membership`,
        method: 'POST',
        body: transformRequestBody({
          loyalty,
          ...body,
        }),
      }),
      // transformResponse,
      invalidatesTags: ['Membership'],
    }),

    /**
     * Update membership
     */
    updateMembership: builder.mutation({
      query: ({ loyalty, ...body }) => ({
        url: `membership`,
        method: 'PUT',
        body: {
          ...transformRequestBody({
            loyalty,
            ...body,
          }),
        },
      }),
      // transformResponse,
      invalidatesTags: ['Membership'],
    }),

    /**
     * Create payment session
     */
    createPaymentSession: builder.mutation<
      TransformedPaymentResponse,
      PaymentRequest
    >({
      query: (body) => ({
        url: `/sessions/payment`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: PaymentResponse) => ({
        paymentSession: response?.response || null,
        httpCode: response?.http_code || null,
      }),
    }),
    /**
     * Create payment session
     */
    completeOrderUsingExistingCard: builder.mutation({
      query: ({
        membershipIds = [],
        coupons = [],
        terminalSession = '',
        skip_mebership_validation = false,
        landingpage = '', // Add the new optional query parameter
      }) => ({
        url: `/user/me/complete-order`,
        method: 'POST',
        body: {
          memberships: membershipIds,
          ...(coupons.length && {
            coupons,
          }),
          terminalSession,
          skip_mebership_validation,
          ...(landingpage && { landingpage }), // Include landingpage if provided
        },
      }),
      transformResponse,
    }),

    /**
     * Get invoice
     */
    getInvoice: builder.query({
      query: ({ id, vat, campaignId }) => {
        let url = `/order/${id}/invoice-summary?vat=${vat ? 0 : 1}`;
        if (campaignId) {
          url += `&campaign_id=${campaignId}`;
        }
        return url;
      },
      transformResponse,
      providesTags: (_result, _error, id) => [{ type: 'Invoice', id }],
    }),

    /**
     * Remove user from membership if logged in as wrong user during checkout
     */
    removeUserFromMembership: builder.mutation({
      query: (body) => ({
        url: '/membership/remove-user',
        method: 'PUT',
        body,
      }),
    }),

    /**
     * Get products
     */
    getProducts: builder.query({
      query: () => '/products',
      transformResponse,
    }),

    /**
     * Get dunning info and amount
     */
    getDunningInfo: builder.query({
      query: (id) => `/user/dunning/${id}`,
      transformResponse,
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetSummariesMutation,
  useCreateMembershipMutation,
  useUpdateMembershipMutation,
  useCreatePaymentSessionMutation,
  useGetInvoiceQuery,
  useCompleteOrderUsingExistingCardMutation,
  useRemoveUserFromMembershipMutation,
  useGetProductsQuery,
  useGetDunningInfoQuery,
} = checkoutApi;
